import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX, Play } from 'lucide-react';

export const VideoSection = ({ src, poster, testId }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [needsTap, setNeedsTap] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      const p = v.play();
      if (p) p.catch(() => setNeedsTap(true));
    };
    if (v.readyState >= 2) tryPlay();
    else v.addEventListener('loadeddata', tryPlay, { once: true });
    return () => v.removeEventListener('loadeddata', tryPlay);
  }, []);

  const handlePlayTap = () => {
    const v = videoRef.current;
    if (v) {
      v.play().then(() => setNeedsTap(false)).catch(() => {});
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section className="relative py-12 px-6 lg:px-12 bg-black" data-testid={testId}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="relative aspect-video rounded-lg overflow-hidden bg-white/5"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster={poster}
            className="w-full h-full object-cover"
            data-testid={`${testId}-player`}
          >
            <source src={src} type="video/mp4" />
          </video>

          {needsTap && (
            <button
              onClick={handlePlayTap}
              className="absolute inset-0 z-20 flex items-center justify-center bg-black/40"
              data-testid={`${testId}-play-button`}
            >
              <span className="p-6 bg-black/60 backdrop-blur-md border border-white/30 rounded-full hover:bg-black/80 transition-all">
                <Play size={32} className="text-white" fill="white" />
              </span>
            </button>
          )}

          <motion.button
            onClick={toggleMute}
            className="absolute top-4 right-4 z-20 p-3 bg-black/60 backdrop-blur-md border border-white/20 rounded-full hover:bg-black/80 transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            data-testid={`${testId}-mute-toggle`}
          >
            {isMuted ? (
              <VolumeX size={20} className="text-white" />
            ) : (
              <Volume2 size={20} className="text-white" />
            )}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};
